import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@vtak/shared';
import { fetchJson } from '@/services/api-client';
import { useCountdownStore } from '@/stores/countdown-store';
import { CountdownDisplay } from './countdown-display';
import { cn } from '@/lib/utils';

/** Minimal launch fields the countdown needs (from GET /api/space/launches). */
interface LaunchLite {
  id: string;
  name: string;
  net: string;
  padName: string;
  provider: string;
}

/** Resolve the active T-0 epoch from the current mode/selection. */
function resolveTarget(
  mode: string,
  manualT0: string | null,
  selectedLaunchId: string | null,
  launches: LaunchLite[],
): number | null {
  if (mode === 'manual') {
    if (!manualT0) return null;
    const parsed = Date.parse(manualT0);
    return Number.isNaN(parsed) ? null : parsed;
  }
  const launch = launches.find((l) => l.id === selectedLaunchId);
  if (!launch) return null;
  const parsed = Date.parse(launch.net);
  return Number.isNaN(parsed) ? null : parsed;
}

/** Range-operations countdown clock (core panel). */
export function CountdownPanel(): React.JSX.Element {
  const { mode, manualT0, selectedLaunchId } = useCountdownStore();
  const selectLaunch = useCountdownStore((s) => s.selectLaunch);
  const setManualT0 = useCountdownStore((s) => s.setManualT0);
  const startTicking = useCountdownStore((s) => s.startTicking);
  const stopTicking = useCountdownStore((s) => s.stopTicking);

  useEffect(() => {
    startTicking();
    return () => stopTicking();
  }, [startTicking, stopTicking]);

  const launchesQuery = useQuery({
    queryKey: queryKeys.space.launches,
    queryFn: () => fetchJson<LaunchLite[]>('/api/space/launches', []),
  });
  const launches = launchesQuery.data ?? [];
  const target = resolveTarget(mode, manualT0, selectedLaunchId, launches);

  return (
    <div className="flex flex-1 flex-col overflow-y-auto p-4">
      <CountdownDisplay targetMs={target} />

      <LaunchPicker
        launches={launches}
        loading={launchesQuery.isLoading}
        activeId={mode === 'launch' ? selectedLaunchId : null}
        onSelect={selectLaunch}
      />

      <ManualEntry value={mode === 'manual' ? manualT0 : null} onChange={setManualT0} />
    </div>
  );
}

/** Selectable list of upcoming launches as the T-0 source. */
function LaunchPicker({
  launches,
  loading,
  activeId,
  onSelect,
}: {
  launches: LaunchLite[];
  loading: boolean;
  activeId: string | null;
  onSelect: (id: string) => void;
}): React.JSX.Element {
  return (
    <section className="mt-2">
      <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        Upcoming Launches
      </h3>
      {loading && <p className="text-xs text-muted-foreground">Loading launches…</p>}
      {!loading && launches.length === 0 && (
        <p className="text-xs text-muted-foreground">No upcoming launches — set a manual T-0 below.</p>
      )}
      <div className="flex flex-col gap-1">
        {launches.map((launch) => (
          <button
            key={launch.id}
            onClick={() => onSelect(launch.id)}
            className={cn(
              'rounded-md border px-3 py-2 text-left transition-colors',
              activeId === launch.id
                ? 'border-echo-yellow/40 bg-echo-yellow/10'
                : 'border-echo-border bg-white/[0.02] hover:bg-white/[0.05]',
            )}
          >
            <p className="truncate text-xs font-semibold text-foreground">{launch.name}</p>
            <p className="truncate text-[10px] text-muted-foreground">
              {launch.provider} · {launch.padName}
            </p>
          </button>
        ))}
      </div>
    </section>
  );
}

/** Manual T-0 entry as a count-down fallback. */
function ManualEntry({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (iso: string | null) => void;
}): React.JSX.Element {
  return (
    <section className="mt-4 border-t border-echo-border pt-4">
      <h3 className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        Manual T-0
      </h3>
      <input
        type="datetime-local"
        value={value ? toLocalInput(value) : ''}
        onChange={(e) => onChange(fromLocalInput(e.target.value))}
        className="w-full rounded-md border border-echo-border bg-white/[0.02] px-3 py-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-echo-yellow"
      />
    </section>
  );
}

/** ISO → value for a datetime-local input (local time, no seconds). */
function toLocalInput(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const offsetMs = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 16);
}

/** datetime-local value → ISO, or null when cleared/invalid. */
function fromLocalInput(local: string): string | null {
  if (!local) return null;
  const parsed = Date.parse(local);
  return Number.isNaN(parsed) ? null : new Date(parsed).toISOString();
}
